// ============================================================
// STATE MANAGER — Server-side authoritative API
// ============================================================
//
// Public surface exposed to the network transport layer. All
// mutating methods return a NEW ServerState; the manager itself
// only holds a reference so callers that want a stateful wrapper
// have an ergonomic path, while Phase 3 tests can still drive the
// pure functions directly.
//
// Responsibilities covered (from session brief):
//   1. validate swap (Phase 1 RequestSwap)
//   2. maintain RoundMatchStats per team
//   3. execute the 3-Tick Execution phase (pure, deterministic)
//
// The current single-machine BattleDirector remains the Client's
// production path; StateManager is consumed by the Phase 3 server
// and by unit tests.

import type { BattleUnit, Base } from '../BattleUnit.ts';
import type { LeaderId } from '../constants.ts';
import { getLeader } from '../balance.ts';
import { cloneState } from './clone.ts';
import { executeExec, type ExecResult } from './tick.ts';
import { updateStats } from './match3Stats.ts';
import {
  validateSchedule,
  validateSpellCommand,
  validateSwap,
} from './validation.ts';
import { generateTerrain } from './terrain.ts';
import type {
  BattleEvent,
  RoundMatchStats,
  ServerState,
  SpellCommand,
  SwapRequest,
  TickCommands,
  ValidationResult,
} from './types.ts';
import { spellsForTeam } from './types.ts';

export class StateManager {
  /** Authoritative battle state. */
  state: ServerState;

  constructor(initialUnits: BattleUnit[], initialBases: Base[]) {
    this.state = {
      units: initialUnits,
      bases: initialBases,
      phase: 'match3',
      round: 0,
      playerStats: {},
      aiStats: {},
      playerUsedEls: [],
      aiUsedEls: [],
    };
    // Normalise via cloneState so downstream mutations never leak into
    // caller-owned unit instances.
    this.state = cloneState(this.state);
  }

  // ---------------------------------------------------------------
  // Terrain
  // ---------------------------------------------------------------

  /**
   * Stamp a terrain seed onto the state and regenerate `state.terrain`
   * deterministically from it. Idempotent — calling twice with the same
   * seed yields the same grid; calling with a different seed replaces
   * the grid entirely.
   *
   * Callers (single: LocalServerAdapter / main.ts handleInitBattle;
   * PvP: RoomBattle.tryStart) invoke this once per battle, right after
   * construction. The pure function is also exported by `terrain.ts` so
   * clients can compute their own copy from the same seed without
   * involving the StateManager.
   */
  applyTerrainSeed(seed: number): void {
    const s = (seed >>> 0);
    this.state.terrainSeed = s;
    this.state.terrain = generateTerrain(s);
  }

  /**
   * Session 9 — record each team's chosen leader. Called once per battle
   * by the transport layer right after construction (single mode via
   * LocalServerAdapter.initBattle; PvP via RoomBattle.tryStart). Drives
   * `validateSpellCommand` / `validateSchedule` so each team can only
   * cast spells inside their leader's `spellElements` set.
   *
   * Pass `undefined` to clear (no-op leader gating — back-compat for
   * tests / legacy fixtures that don't care about leaders).
   */
  applyLeaders(leaderByTeam: [LeaderId, LeaderId] | undefined): void {
    if (leaderByTeam) {
      // Validate both ids resolve to a real leader; `getLeader` falls
      // back to DEFAULT_LEADER if unknown so we never persist garbage.
      const t0 = getLeader(leaderByTeam[0]).id;
      const t1 = getLeader(leaderByTeam[1]).id;
      this.state.leaderByTeam = [t0, t1];
    } else {
      this.state.leaderByTeam = undefined;
    }
  }

  /**
   * Helper for validation paths — turn a team's leader id into the
   * allowed spellElements array, or `undefined` if no leader is set
   * (= no leader gating, all ELEM ids allowed).
   */
  private _allowedElsFor(team: 0 | 1): ReadonlyArray<number> | undefined {
    const ldr = this.state.leaderByTeam?.[team];
    if (!ldr) return undefined;
    return getLeader(ldr).spellElements;
  }

  // ---------------------------------------------------------------
  // Phase 1: Match-3
  // ---------------------------------------------------------------

  /** SRS §2.7 RequestSwap. Server-authoritative; does not execute the swap. */
  validateSwap(req: SwapRequest): ValidationResult {
    if (this.state.phase !== 'match3') {
      return { valid: false, reason: 'not in match3 phase' };
    }
    return validateSwap(req);
  }

  /**
   * Accumulate per-element point contributions from a swap's resulting
   * cascade (Points & Tiers system). `matchStats` is the per-element
   * point sum produced by the chain — on the client this comes from
   * `M3Board._onChainComplete.pointsByElement`. Returns a new state.
   *
   * NOTE: in PvP, callers should prefer the server-replay path
   * (`reconcileMatchResults` → mirror.applyRound) to compute authoritative
   * stats, then pass the resulting effectiveStats here.
   */
  commitMatchResults(
    team: 0 | 1,
    matchStats: Record<string | number, number>,
  ): ServerState {
    const next = cloneState(this.state);
    if (team === 0) next.playerStats = updateStats(next.playerStats, matchStats);
    else next.aiStats = updateStats(next.aiStats, matchStats);
    this.state = next;
    return next;
  }

  /** Snapshot of the current round's accumulated points pool for either team. */
  getRoundStats(team: 0 | 1): RoundMatchStats {
    return { ...(team === 0 ? this.state.playerStats : this.state.aiStats) };
  }

  /** Transition to scheduling phase (called when the 15s MATCH3 timer expires). */
  enterScheduling(): ServerState {
    const next = cloneState(this.state);
    next.phase = 'sched';
    this.state = next;
    return next;
  }

  // ---------------------------------------------------------------
  // Phase 2: Spell scheduling
  // ---------------------------------------------------------------

  validateSpellCommand(team: 0 | 1, cmd: SpellCommand): ValidationResult {
    if (this.state.phase !== 'sched') {
      return { valid: false, reason: 'not in sched phase' };
    }
    const stats = team === 0 ? this.state.playerStats : this.state.aiStats;
    const used = team === 0 ? this.state.playerUsedEls : this.state.aiUsedEls;
    return validateSpellCommand(cmd, stats, used, this._allowedElsFor(team));
  }

  validateSchedule(team: 0 | 1, schedule: SpellCommand[]): ValidationResult {
    const stats = team === 0 ? this.state.playerStats : this.state.aiStats;
    return validateSchedule(schedule, stats, this._allowedElsFor(team));
  }

  // ---------------------------------------------------------------
  // Phase 3: Execution
  // ---------------------------------------------------------------

  /**
   * Run the 3-Tick deterministic execution and return the event log.
   * Mutates internal state; returns the new state and events.
   *
   * Equivalent to BattleDirector.buildTimeline + Save/Restore in a
   * single call — but here the snapshot-and-restore is baked into
   * `cloneState`, and the emitted `BattleEvent[]` is plain data that
   * can be serialised or replayed deterministically.
   */
  executeExec(cmds: TickCommands): { events: BattleEvent[]; winner: number } {
    // Validate both schedules up front so a malformed plan from EITHER
    // side can't silently clobber the game. Phase 3d: in PvP, either
    // team can be the offender — error labels say "team N" so the
    // message doesn't read as if it's always the AI.
    for (const team of [0, 1] as const) {
      const stats = team === 0 ? this.state.playerStats : this.state.aiStats;
      const res = validateSchedule(spellsForTeam(cmds, team), stats, this._allowedElsFor(team));
      if (!res.valid) throw new Error(`invalid team ${team} schedule: ${res.reason}`);
    }

    const result: ExecResult = executeExec(this.state, cmds);
    this.state = result.newState;
    return { events: result.events, winner: result.winner };
  }

  /**
   * Stateless variant — same logic as `executeExec` but does not touch
   * `this.state`. Handy for speculative simulation and tests.
   */
  static executeExec(state: ServerState, cmds: TickCommands): ExecResult {
    return executeExec(state, cmds);
  }
}
